/** Number and size formatting for instrument readouts (tabular, unit-explicit, never "AI-ish"). */

const nf = new Intl.NumberFormat('en-US')

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  return nf.format(n)
}

/** 1,234,567 → 1.23M; keeps small numbers exact. */
export function formatCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const a = Math.abs(n)
  if (a < 10_000) return nf.format(n)
  if (a < 1_000_000) return `${(n / 1000).toFixed(a < 100_000 ? 1 : 0)}k`
  if (a < 1_000_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  return `${(n / 1_000_000_000).toFixed(2)}B`
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${i === 0 ? v.toFixed(0) : v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`
}

export function formatPercent(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

/** Integer micro-units (ledger) → points with fixed decimals; never floats in the money path. */
export function formatMicro(micro: number | bigint | string | null | undefined, decimals = 2): string {
  if (micro === null || micro === undefined || micro === '') return '—'
  let big: bigint
  try {
    big = BigInt(micro as never)
  } catch {
    return '—'
  }
  const neg = big < 0n
  const abs = neg ? -big : big
  const whole = abs / 1_000_000n
  const frac = (abs % 1_000_000n).toString().padStart(6, '0').slice(0, decimals)
  return `${neg ? '−' : ''}${nf.format(Number(whole))}${decimals > 0 ? `.${frac}` : ''}`
}

/** Initials for a square avatar: "Ada Lovelace" → "AL", "ada@example.com" → "AD". */
export function initials(name: string | null | undefined): string {
  const s = (name ?? '').trim()
  if (!s) return '··'
  const words = s.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean)
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
  return s.slice(0, 2).toUpperCase()
}

export function pluralize(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`
}
